import * as Yup from 'yup';
import { Endpoints } from '@/api/ApiEndpoints';

const validationSchema = Yup.object().shape({
	slider: Yup.array().of(
		Yup.object().shape({
			image: Yup.mixed()
				.when('type', {
					is: (type: string) => type !== 'mission' && type !== 'subscription' && type !== 'countryLink',
					then: (schema) => schema.required(),
					otherwise: (schema) => schema.nullable(),
				})
				.label('Image'),
		})
	),
});

const sliderTypeOptions = [
	{ id: 'game', name: 'Game' },
	{ id: 'mission', name: 'Mission' },
	{ id: 'external', name: 'External Link' },
	{ id: 'countryLink', name: 'Country Link' },
	{ id: 'subscription', name: 'Subscription Banner' },
];

const Inputs = [
	{
		name: 'missions',
		label: 'Mission of the Day',
		type: 'select',
		value: 'single',
		valueAttr: 'missionOfTheDayId',
		required: false,
	},
	{
		name: 'slider',
		label: 'Slider',
		type: 'nestedArray',
		value: 'multiple',
		reOrder: "reOrder",
		valueAttr: 'slider',
		collapsible: true,
		summaryFn: (row: any, relationships: Record<string, any[]> | undefined) => {
			if (row.type === 'game' && row.sliderGame && relationships?.games) {
				const game = relationships.games.find((g: any) => g.id === row.sliderGame || g.id === Number(row.sliderGame));
				return game?.name || `Game #${row.sliderGame}`;
			}
			if (row.type === 'mission' && row.missionId && relationships?.missions) {
				const mission = relationships.missions.find((m: any) => m.id === row.missionId || m.id === Number(row.missionId));
				return mission?.name || `Mission #${row.missionId}`;
			}
			if (row.type === 'external') {
				return row.title || row.externalUrl || 'External Link';
			}
			return '';
		},
		columns: [
			{
				name: 'image',
				label: 'Image',
				type: 'image',
				value: 'single',
				valueAttr: 'image',
				required: false,
				defaultValue: '',
				visibleWhen: (row: any) => row.type !== 'countryLink',
				transform: (path: string) => {
					const safePath = path.replace(" ","%20");
					if (path?.includes?.('blob')) {
						return safePath;
					}
					return process.env.VUE_APP_ASSETS_URL + '/' + safePath;
				},
				generateAction: {
					label: 'Generate AI Cover',
					visibleWhen: (row: any, relationships: Record<string, any[]> | undefined) => {
						if (row.type === 'game' && row.sliderGame) return true;
						if (row.type === 'mission' && row.missionId) {
							const mission = relationships?.missions?.find((m: any) => m.id === row.missionId || m.id === Number(row.missionId));
							return mission?.type === 'PlaceBetOnSpecificGame' && mission?.targetValue;
						}
						return false;
					},
					endpoint: Endpoints.GENERATE_MATCH_COVER,
					getParam: (row: any, relationships: Record<string, any[]> | undefined) => {
						if (row.type === 'game') {
							const game = relationships?.games?.find((g: any) => g.id === row.sliderGame || g.id === Number(row.sliderGame));
							return game?.providerId ?? row.sliderGame;
						}
						if (row.type === 'mission') {
							const mission = relationships?.missions?.find((m: any) => m.id === row.missionId || m.id === Number(row.missionId));
							return mission?.targetValue;
						}
						return null;
					},
					resultKey: 'imageUrl',
				},
			},
			{
				name: 'type',
				label: 'Type',
				type: 'select',
				value: 'single',
				valueAttr: 'type',
				required: true,
				defaultValue: 'game',
				options: sliderTypeOptions,
				isLocal: true,
			},
			{
				name: 'sliderGame',
				label: 'Game',
				type: 'select',
				value: 'single',
				valueAttr: 'sliderGame',
				isPicker: true,
				relationshipName: 'games',
				hideSelect: true,
				visibleWhen: (row) => row.type === 'game',
				onChangeCallback: (row: any, val: any, relationships: Record<string, any[]> | undefined) => {
					const game = relationships?.games?.find((g: any) => g.id === val || g.id === Number(val));
					if (game?.gameDate) {
						const gameDate = new Date(game.gameDate);
						gameDate.setDate(gameDate.getDate() + 1);
						row.endTime = gameDate;
					}
				},
			},
			{
				name: 'missionId',
				label: 'Mission',
				type: 'select',
				value: 'single',
				valueAttr: 'missionId',
				relationshipName: 'missions',
				visibleWhen: (row) => row.type === 'mission',
				onChangeCallback: (row: any, val: any, relationships: Record<string, any[]> | undefined) => {
					const mission = relationships?.missions?.find((m: any) => m.id === val || m.id === Number(val));
					if (mission?.endDate) {
						row.endTime = new Date(mission.endDate);
					}
				},
			},
			{
				name: 'externalUrl',
				label: 'External URL',
				type: 'text',
				value: 'single',
				valueAttr: 'externalUrl',
				visibleWhen: (row) => row.type === 'external',
			},
			{
				name: 'title',
				label: 'Title',
				type: 'text',
				value: 'single',
				valueAttr: 'title',
				visibleWhen: (row) => row.type === 'external',
			},
			{
				name: 'description',
				label: 'Description',
				type: 'textarea',
				value: 'single',
				valueAttr: 'description',
				visibleWhen: (row) => row.type === 'external',
			},
			{
				name: 'startTime',
				label: 'Start Date',
				type: 'date',
				value: 'single',
				valueAttr: 'startTime',
			},
			{
				name: 'endTime',
				label: 'End Date',
				type: 'date',
				value: 'single',
				valueAttr: 'endTime',
			},
			{
				name: 'countryIds',
				label: 'Countries (empty = everywhere)',
				type: 'select',
				value: 'multiple',
				valueAttr: 'countryIds',
				relationshipName: 'countryIds',
				enableOrder: false,
			},
		],
	}
];

export { Inputs, validationSchema };
